import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { App } from '@galaxy/marketplace-apps';
import { LineItem } from '@vendasta/sales-orders';
import { SalesOpportunitiesService } from '@vendasta/sales-opportunities';
import { Package } from '@vendasta/marketplace-packages';
import { LineItemsSelected, SalesOpportunity } from './line-items.interface';
import { convertSelectedAppToLineItem, packageToUILineItem } from '../../shared/utils';
import { UILineItemWithEditionPricing } from '../../shared/interface';
import { AccountGroup } from '@galaxy/account-group';
import { UILineItem } from '@vendasta/store';
import { MerchantService } from '@galaxy/billing';
import { LineItemsAdapterService } from './line-items-adapter.service';

@Injectable()
export class LineItemsService {
  partnerId: string;
  marketId: string;
  businessId: string;

  specifiedLineItems$$: BehaviorSubject<LineItem[]> = new BehaviorSubject<LineItem[]>([]);
  specifiedLineItems$: Observable<LineItem[]> = this.specifiedLineItems$$.asObservable();
  uiLineItems$: Observable<UILineItemWithEditionPricing[]>;

  loading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  loading$: Observable<boolean> = this.loading$$.asObservable();

  // Find packages and apps that have been deselected and remove them from the service instance
  public static removeUnselectedLineItems(
    preselectedPackages: Package[],
    preselectedApps: App[],
    newlySelectedLineItems: LineItemsSelected,
    lineItemsToUpdate: UILineItemWithEditionPricing[],
  ): UILineItemWithEditionPricing[] {
    // Find packages that have been deselected in the selector
    const deselectedPackageIds = [];
    preselectedPackages.map((pkg) => {
      const stillSelected = newlySelectedLineItems.packages.some((selected) => selected.packageId === pkg.packageId);
      if (!stillSelected) {
        deselectedPackageIds.push(pkg.packageId);
      }
    });

    // Find apps that have been deselected in the selector
    const deselectedAppIds = [];
    preselectedApps.map((app) => {
      const stillSelected = newlySelectedLineItems.apps.some((selected) => selected.key.appId === app.key.appId);
      if (!stillSelected) {
        deselectedAppIds.push(app.key.appId);
      }
    });

    // Remove packages and apps that have been deselected in the selector
    return lineItemsToUpdate.filter(
      (item) =>
        (!!item.lineItem.packageId && !deselectedPackageIds.includes(item.lineItem.packageId)) ||
        (!!item.lineItem.appKey?.appId && !deselectedAppIds.includes(item.lineItem.appKey.appId)),
    );
  }

  constructor(
    @Inject(SalesOpportunitiesService) private readonly salesOpportunityService: SalesOpportunity,
    private readonly merchantService: MerchantService,
    private readonly lineItemsAdapterService: LineItemsAdapterService,
  ) {}

  // initializes service class variables
  initialize(business: Pick<AccountGroup, 'externalIdentifiers'>, lineItems: LineItem[]): void {
    this.marketId = business.externalIdentifiers.marketId;
    this.partnerId = business.externalIdentifiers.partnerId;
    this.specifiedLineItems$$.next(lineItems || []);
    this.buildLineItemsFromMarketplaceInfo();
  }

  // populates line item branding info from marketplace package and/or app APIs
  private buildLineItemsFromMarketplaceInfo(): void {
    this.uiLineItems$ = this.lineItemsAdapterService.lineItemsToUILineItems({
      lineItems$: this.specifiedLineItems$,
      partnerId: this.partnerId,
      marketId: this.marketId,
    });
  }

  addSpecifiedLineItems(lineItems: LineItem[]): void {
    this.specifiedLineItems$$.next(this.specifiedLineItems$$.getValue().concat(lineItems));
  }

  private getDefaultCurrency(): Observable<string> {
    if (this.marketId && this.partnerId) {
      return this.merchantService.getMultiRetailConfigurations(this.partnerId, [this.marketId]).pipe(
        map((retailConfigs) => {
          return retailConfigs.get(this.marketId).currencyCode;
        }),
      );
    }
    return of('');
  }

  // resolves the latest line items according to what was selected
  public resolveLineItemSelectorInteractions(
    preselectedPackages: Package[],
    preselectedApps: App[],
    newlySelectedLineItems: LineItemsSelected,
    lineItemsToUpdate: UILineItemWithEditionPricing[],
  ): Observable<UILineItemWithEditionPricing[]> {
    lineItemsToUpdate = LineItemsService.removeUnselectedLineItems(
      preselectedPackages,
      preselectedApps,
      newlySelectedLineItems,
      lineItemsToUpdate,
    );
    const addNewlySelectedPackages$ = this.addNewlySelectedPackages(newlySelectedLineItems, lineItemsToUpdate);
    return addNewlySelectedPackages$.pipe(
      mergeMap((lineItems) => {
        return this.addNewlySelectedApps(newlySelectedLineItems, lineItems);
      }),
    );
  }

  // Add only newly selected packages so that previously selected packages do not have quantity and revenue overwritten
  public addNewlySelectedPackages(
    newlySelectedLineItems: LineItemsSelected,
    lineItemsToUpdate: UILineItemWithEditionPricing[],
  ): Observable<UILineItem[]> {
    return this.getDefaultCurrency().pipe(
      map((currency) => {
        const addedPackages = newlySelectedLineItems.packages.reduce((added, pkg) => {
          if (!lineItemsToUpdate.some((item) => item.lineItem.packageId === pkg.packageId)) {
            added.push(pkg);
          }
          return added;
        }, []);
        return lineItemsToUpdate.concat(addedPackages.map((pkg) => packageToUILineItem(pkg, currency)));
      }),
    );
  }

  // Add only newly added apps so that preselected apps that have not be deselected do not have quantity and revenue overwritten
  public addNewlySelectedApps(
    newlySelectedLineItems: LineItemsSelected,
    lineItemsToUpdate: UILineItemWithEditionPricing[],
  ): Observable<UILineItem[]> {
    return this.getDefaultCurrency().pipe(
      map((currency) => {
        const addedApps = newlySelectedLineItems.apps.reduce((added, app) => {
          if (!lineItemsToUpdate.some((item) => item.lineItem.appKey?.appId === app.key.appId)) {
            added.push(app);
          } else {
            const mustUpdateIndex = lineItemsToUpdate.findIndex((u) => u.lineItem.appKey?.appId === app.key.appId);
            if (
              mustUpdateIndex > -1 &&
              lineItemsToUpdate[mustUpdateIndex].lineItem.appKey?.editionId !== app.key.editionId
            ) {
              lineItemsToUpdate[mustUpdateIndex] = convertSelectedAppToLineItem(app, currency);
            }
          }
          return added;
        }, []);
        return lineItemsToUpdate.concat(addedApps.map((app) => convertSelectedAppToLineItem(app, currency)));
      }),
    );
  }
}
